/**
 * Single advisor row in the sidebar / drawer / View-all modal.
 *
 * Renders avatar + name + expertise, plus a crown on subscription-only
 * advisors. Owns no state — the parent's `onTap` handles the action (start
 * new chat with this advisor) and is responsible for closing the drawer /
 * modal if relevant.
 */
import { CrownSimple } from '@phosphor-icons/react';
import classNames from 'classnames';

import { isPremiumAdvisor } from '../../../service/advisorAccess';
import Avatar from '../Avatar';
import styles from './styles.module.scss';

const AdvisorRow = ({ advisor, onTap, disabled }) => {
    const onClick = () => {
        if (disabled) return;
        onTap?.(advisor.alias);
    };

    const onKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTap?.(advisor.alias);
        }
    };

    // Fallback initials when the catalogue hasn't been fetched yet (or
    // the advisor doc has no chat / preview image) — Avatar renders a
    // blank ring with empty src, which looks like a broken image.
    const initial = (advisor.name?.[0] || '?').toLowerCase();

    const isPremium = isPremiumAdvisor(advisor.alias);

    // `aria-label` overrides the row's inner content for the accessible name,
    // so the crown below would be silent to a screen reader if it weren't
    // folded in here. Untranslated to match the base label — this file
    // predates i18n on these strings.
    const ariaLabel = isPremium ? `Start chat with ${advisor.name} (premium advisor)` : `Start chat with ${advisor.name}`;

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={classNames(styles.advisorRow, { [styles.advisorRowDisabled]: disabled })}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
        >
            {advisor.avatar ? (
                <Avatar imageSrc={advisor.avatar} size={40} borderType="blue" nativeClassName={styles.advisorAvatarSquare} />
            ) : (
                <div className={styles.advisorAvatarFallback} aria-hidden="true">
                    {initial}
                </div>
            )}
            <div className={styles.advisorRowText}>
                <span className={styles.advisorRowName}>{advisor.name?.toLowerCase()}</span>
                {advisor.expertise && <span className={styles.advisorRowRole}>{advisor.expertise}</span>}
            </div>
            {/* HORO-10581: subscription-only advisors carry the same crown as
                CatalogPageV2's PremiumTag. A LOCK icon here would be wrong —
                LockedTag/`showLockedTag` reserve that one for the REGISTER
                gate, so it reads as "just sign up", the opposite of the
                signal this row needs. Describes the ADVISOR, not the viewer,
                so a paying subscriber still sees it (same rationale as
                `showPremiumTag` in advisorCardModel.js). Load-bearing now
                that Zara is pinned to the top of this list: without it her
                row is indistinguishable from the free advisors under it,
                and the first feedback is the paywall modal on tap. */}
            {isPremium && (
                <span className={styles.premiumCrown} aria-hidden="true">
                    <CrownSimple size={12} weight="fill" color="#FFFFFF" />
                </span>
            )}
        </div>
    );
};

export default AdvisorRow;
